import React from 'react';
import { RefreshCw, Search, Filter, BarChart3 } from 'lucide-react';
import Card from './Card';
import ErrorAlert from './ErrorAlert';
import LoadingSpinner from './LoadingSpinner';
import ActionButton from './ActionButton';

const DataTable = ({
  // Header props
  title,
  subtitle,
  icon: Icon,
  iconColor = 'text-blue-600',
  actions = [], // Array of action buttons [{icon, label, onClick, variant}]
  
  // Filters props
  filters = [], // Array of filter configs
  searchProps = null, // {value, onChange, placeholder}
  onRefresh,
  toolbarActions = [], // Array of icon-only actions for the search toolbar
  
  // Stats props
  showStats = false,
  onToggleStats = null, // Function to toggle stats visibility
  
  // State props
  loading = false,
  error = null,
  success = null,
  
  // Content
  children,
  
  // Layout props
  className = '',
  showFilters = true,
  filtersClassName = ''
}) => {
  if (loading) {
    return <LoadingSpinner center text="Loading..." />;
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header Section */}
      <Card padding="none" rounded="xl">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {Icon && <Icon className={`w-6 h-6 ${iconColor}`} />}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
                {subtitle && <p className="text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {actions.map((action, index) => (
                <ActionButton
                  key={index}
                  icon={action.icon}
                  onClick={action.onClick}
                  variant={action.variant || 'primary'}
                  size={action.size || 'md'}
                  disabled={action.disabled}
                >
                  {action.label}
                </ActionButton>
              ))}
            </div>
          </div>
        </div>

        {/* Filters and Search Section */}
        {showFilters && (searchProps || filters.length > 0 || onRefresh || toolbarActions.length > 0) && (
          <div className={`p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 ${filtersClassName}`}>
            <div className="flex items-center gap-4">
              {/* Left side: Search and Filters */}
              <div className="flex-1 flex items-center gap-4">
                {/* Search Input */}
                {searchProps && (
                  <div className="relative flex-1 max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      placeholder={searchProps.placeholder || "Search..."}
                      value={searchProps.value}
                      onChange={searchProps.onChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}

                {/* Custom Filters */}
                {filters.map((filter, index) => {
                  const FilterIcon = filter.icon || Filter;
                  
                  if (filter.type === 'select') {
                    return (
                      <div key={index} className="relative min-w-48">
                        <FilterIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                        <select
                          value={filter.value}
                          onChange={filter.onChange}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        >
                          {filter.options.map((option, optIndex) => (
                            <option key={optIndex} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else if (filter.type === 'custom') {
                    return <div key={index}>{filter.component}</div>;
                  }
                  return null;
                })}
              </div>

              {/* Right side: Toolbar Actions */}
              {(toolbarActions.length > 0 || onToggleStats || onRefresh) && (
                <div className="flex items-center space-x-1">
                  {/* Custom Toolbar Actions */}
                  {toolbarActions.map((action, index) => (
                    <ActionButton
                      key={index}
                      icon={action.icon}
                      onClick={action.onClick}
                      variant={action.variant || 'secondary'}
                      tooltip={action.tooltip}
                    />
                  ))}
                  
                  {/* Default Stats Toggle Button */}
                  {onToggleStats && (
                    <ActionButton
                      icon={BarChart3}
                      onClick={onToggleStats}
                      variant={showStats ? 'primary' : 'secondary'}
                      tooltip={showStats ? 'Hide Stats' : 'Show Stats'}
                    />
                  )}
                  
                  {/* Default Refresh Button */}
                  {onRefresh && (
                    <ActionButton
                      icon={RefreshCw}
                      onClick={onRefresh}
                      variant="secondary"
                      tooltip="Refresh"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="p-4">
            <ErrorAlert error={error} variant="error" />
          </div>
        )}

        {/* Success Display */}
        {success && (
          <div className="p-4">
            <ErrorAlert error={success} variant="success" />
          </div>
        )}

        {/* Content Area */}
        {children}
      </Card>
    </div>
  );
};

// Table Component for use within DataTable
export const Table = ({ columns, data, onRowClick, rowKey = 'id', className = '' }) => {
  return (
    <div className="overflow-x-auto">
      <table className={`w-full ${className}`}>
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                className={`px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${column.className || ''}`}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {data.map((row, rowIndex) => (
            <tr
              key={row[rowKey] || rowIndex}
              onClick={() => onRowClick && onRowClick(row)}
              className={onRowClick ? 'hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer' : ''}
            >
              {columns.map((column, colIndex) => (
                <td key={colIndex} className={`px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100 ${column.cellClassName || ''}`}>
                  {column.render ? column.render(row) : row[column.accessor]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">No data available</p>
        </div>
      )}
    </div>
  );
};

// List Component for settings-like layouts
export const DataList = ({ data = [], renderItem, emptyState, columns = 1, className = '' }) => {
  if (data.length === 0 && emptyState) {
    return emptyState;
  }

  const gridClassName = columns === 2 
    ? 'grid grid-cols-1 md:grid-cols-2 gap-6' 
    : columns === 3 
    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
    : 'space-y-0 divide-y divide-gray-200';

  if (columns > 1) {
    return (
      <div className={`${gridClassName} ${className}`}>
        {data.map((item, index) => (
          <div key={item.id || index} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            {renderItem(item, index)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`divide-y divide-gray-200 dark:divide-gray-700 ${className}`}>
      {data.map((item, index) => (
        <div key={item.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
          {renderItem(item, index)}
        </div>
      ))}
      {data.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">No items available</p>
        </div>
      )}
    </div>
  );
};

export default DataTable;